import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Globe, X, Check } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', native: 'English', english: 'English' },
  { code: 'de', native: 'Deutsch', english: 'German' },
];

// Component-local strings: the picker is the one place where every language
// must be readable before a language is chosen, so it doesn't belong to any
// page namespace.
const UI_TEXT = {
  en: { title: 'Choose a language', open: 'Choose a language', close: 'Close' },
  de: { title: 'Sprache wählen', open: 'Sprache wählen', close: 'Schließen' },
};

/**
 * Globe icon → modal language picker (Airbnb-style). One central component
 * for every surface with a language choice: /, /business, /business/start,
 * /p/:slug, /p/:slug/dashboard, legal pages.
 *
 * The choice persists in localStorage ("okio_lang") and applies app-wide —
 * any screen migrated to t() keys follows it.
 */
const LanguageSwitcher = ({ className = '' }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);
  const text = UI_TEXT[current] || UI_TEXT.en;

  // Escape closes; page behind the modal shouldn't scroll while it's open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  const select = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={text.open}
        title={text.open}
        className={`p-2 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors ${className}`}
      >
        <Globe className="w-[18px] h-[18px]" strokeWidth={2} />
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-label={text.title}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />

            {/* Panel */}
            <div className="relative w-full max-w-md bg-gray-900 border border-white/10 rounded-3xl shadow-2xl p-6 sm:p-8">
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={text.close}
                className="absolute top-4 left-4 p-2 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={2.5} />
              </button>

              <h2 className="text-white text-xl font-bold text-center mb-6 mt-2">
                {text.title}
              </h2>

              <div className="grid grid-cols-2 gap-3">
                {LANGUAGES.map((lang) => {
                  const active = current === lang.code;
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => select(lang.code)}
                      className={`relative rounded-2xl px-4 py-4 text-left border transition-colors ${
                        active
                          ? 'border-white bg-white/10'
                          : 'border-white/10 hover:border-white/40 hover:bg-white/5'
                      }`}
                    >
                      <span className="block text-white font-semibold">
                        {lang.native}
                      </span>
                      <span className="block text-white/40 text-sm mt-0.5">
                        {lang.english}
                      </span>
                      {active && (
                        <Check
                          className="absolute top-4 right-4 w-4 h-4 text-white"
                          strokeWidth={3}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default LanguageSwitcher;
